import { useId, type ComponentType, type CSSProperties } from 'react';
import { Anchor, Anim, Between, Handle, Handles, keys, LOOP, outline, Walk } from '../stage';

/**
 * Guides for the shapes, type, artboard, slice and measure tools, and the point-choosing
 * modes of Select. Same pacing as the exemplars in basics.tsx: the press lands around 0.12,
 * the gesture ends around 0.6, and the result holds to 0.97.
 */

type Pt = readonly [number, number];

/** A discrete (stepped) animation of one attribute: `values[i]` from `times[i]`. */
function Steps({ attr, values, times }: { attr: string; values: readonly (number | string)[]; times: readonly number[] }) {
  return <animate attributeName={attr} values={values.join(';')} keyTimes={times.join(';')} dur={`${LOOP}s`} repeatCount="indefinite" calcMode="discrete" />;
}

/** A stepped move of its parent: to `at[i]` from `times[i]`. */
function StepMove({ at, times }: { at: readonly Pt[]; times: readonly number[] }) {
  return <animateTransform attributeName="transform" type="translate" values={at.map(([x, y]) => `${x} ${y}`).join(';')} keyTimes={times.join(';')} dur={`${LOOP}s`} repeatCount="indefinite" calcMode="discrete" />;
}

/** A shape before the drag: every point at the press, so animating to its outline grows it from there. */
function collapsed(points: readonly Pt[], from: Pt): string {
  return outline(points.map(() => from));
}

/** Points of a unit shape (in -1..1) fitted exactly into a box. */
function fit(unit: readonly Pt[], x: number, y: number, w: number, h: number): Pt[] {
  const xs = unit.map(([px]) => px);
  const ys = unit.map(([, py]) => py);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  return unit.map(([px, py]) => [x + ((px - x0) / (x1 - x0)) * w, y + ((py - y0) / (y1 - y0)) * h] as const);
}

/** The time the press lands and the drag ends, for the drag-a-box tools. */
const PRESS = 0.14;
const DONE = 0.6;

const accentLine: CSSProperties = { stroke: 'var(--accent)', strokeWidth: 1.5, strokeLinecap: 'round', fill: 'none' };

/** The number a measurement answers with, in an accent pill. */
function Chip({ x, y, text, w }: { x: number; y: number; text: string; w: number }) {
  return (
    <g>
      <rect x={x - w / 2} y={y - 8} width={w} height={16} rx={8} style={{ fill: 'var(--accent)' }} />
      <text x={x} y={y + 3.5} textAnchor="middle" style={{ fill: '#fff', font: '600 10px var(--font-sans)' }}>{text}</text>
    </g>
  );
}

// ——— Select, choosing points ———

/** Direct selection: press an anchor, it turns solid, drag it and the outline follows. */
function SelectPointDemo() {
  const a: Pt = [54, 88];
  const b: Pt = [92, 34];
  const b2: Pt = [118, 14];
  const c: Pt = [164, 40];
  const d: Pt = [188, 88];
  const shape = (p: Pt) => `M${a[0]} ${a[1]}L${p[0]} ${p[1]}Q${(p[0] + c[0]) / 2} ${Math.min(p[1], c[1]) - 10} ${c[0]} ${c[1]}L${d[0]} ${d[1]}Z`;
  return <>
    <path className="g-obj" d={shape(b)}>
      <Anim attr="d" values={[shape(b), shape(b), shape(b2), shape(b2)]} times={[0, 0.36, 0.66, 1]} />
    </path>
    <Anchor x={a[0]} y={a[1]} />
    <Anchor x={c[0]} y={c[1]} />
    <Anchor x={d[0]} y={d[1]} />
    <rect className="g-anchor" x={b[0] - 2.2} y={b[1] - 2.2} width={4.4} height={4.4}>
      <animateTransform attributeName="transform" type="translate" {...keys(['0 0', '0 0', `${b2[0] - b[0]} ${b2[1] - b[1]}`, `${b2[0] - b[0]} ${b2[1] - b[1]}`], [0, 0.36, 0.66, 1])} />
      <Steps attr="class" values={['g-anchor', 'g-anchor g-anchor-on', 'g-anchor']} times={[0, 0.3, 0.97]} />
    </rect>
    <Walk k="select:point" stops={[[196, 104, 0.04], [b[0], b[1], 0.28], [b[0], b[1], 0.36], [b2[0], b2[1], 0.66]]} />
  </>;
}

/** The open path both choosing modes pick points from. */
const ZIG: Pt[] = [[30, 78], [75, 36], [120, 78], [165, 36], [210, 78]];
const zigPath = ZIG.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join('');

function ZigAnchors({ chosenAt }: { chosenAt: number }) {
  return <>
    {ZIG.map(([x, y], i) => i === 2 || i === 3
      ? (
        <rect key={i} className="g-anchor" x={x - 2.2} y={y - 2.2} width={4.4} height={4.4}>
          <Steps attr="class" values={['g-anchor', 'g-anchor g-anchor-on', 'g-anchor']} times={[0, chosenAt, 0.97]} />
        </rect>
      )
      : <Anchor key={i} x={x} y={y} />)}
  </>;
}

/** Marquee: drag a dashed box round some anchors and those are chosen. */
function SelectAreaDemo() {
  const [x, y, w, h] = [100, 20, 86, 76];
  return <>
    <path className="g-path" d={zigPath} />
    <rect className="g-band" x={x} y={y}>
      <Anim attr="width" values={[0, 0, w, w]} times={[0, PRESS, DONE - 0.04, 1]} />
      <Anim attr="height" values={[0, 0, h, h]} times={[0, PRESS, DONE - 0.04, 1]} />
      <Between from={PRESS} to={0.97} />
    </rect>
    <ZigAnchors chosenAt={0.46} />
    <Walk k="select:area" stops={[[x, y, 0.12], [x + w, y + h, DONE - 0.04]]} />
  </>;
}

/** Lasso: draw a free loop round some anchors and those are chosen. */
function SelectLassoDemo() {
  // A hand-drawn loop round the middle two anchors, starting above-left and closing on itself.
  const loop: Pt[] = [[104, 30], [126, 20], [152, 16], [178, 22], [190, 40], [184, 60], [160, 72], [146, 90], [124, 96], [104, 90], [98, 70], [104, 50], [108, 32]];
  const d = loop.map(([x, y], i) => `${i ? 'L' : 'M'}${x} ${y}`).join('');
  const start = PRESS;
  const end = DONE - 0.02;
  // The walk keeps the cursor on the loop at the same rate the stroke is revealed.
  let total = 0;
  const lens = [0];
  for (let i = 1; i < loop.length; i++) { total += Math.hypot(loop[i]![0] - loop[i - 1]![0], loop[i]![1] - loop[i - 1]![1]); lens.push(total); }
  const stops = loop.map(([x, y], i) => [x, y, Number((start + (lens[i]! / total) * (end - start)).toFixed(4))] as const);
  return <>
    <path className="g-path" d={zigPath} />
    <path d={`${d}Z`} style={{ fill: 'color-mix(in srgb, var(--accent) 10%, transparent)', stroke: 'none' }}>
      <Between from={end} to={0.97} />
    </path>
    <path d={d} pathLength={1} strokeDasharray="1 3" style={{ fill: 'none', stroke: 'var(--accent)', strokeWidth: 1.2, strokeLinejoin: 'round' }}>
      <Anim attr="stroke-dashoffset" values={[1.2, 1.2, 1, 0, 0]} times={[0, start - 0.01, start, end, 1]} />
    </path>
    <ZigAnchors chosenAt={end} />
    <Walk k="select:lasso" stops={[[70, 104, 0.02], ...stops]} />
  </>;
}

// ——— Shapes ———

/** The chosen box a new shape sits in once the drag ends. */
function Chosen({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return <g><Between from={DONE + 0.02} to={0.97} /><rect className="g-sel" x={x} y={y} width={w} height={h} /><Handles x={x} y={y} w={w} h={h} /></g>;
}

/** Ellipse: drag from corner to corner and the ellipse fills the box. */
function EllipseDemo() {
  const [x, y, w, h] = [64, 22, 112, 68];
  const t = [0, PRESS, DONE, 1];
  return <>
    <ellipse className="g-new">
      <Anim attr="cx" values={[x, x, x + w / 2, x + w / 2]} times={t} />
      <Anim attr="cy" values={[y, y, y + h / 2, y + h / 2]} times={t} />
      <Anim attr="rx" values={[0, 0, w / 2, w / 2]} times={t} />
      <Anim attr="ry" values={[0, 0, h / 2, h / 2]} times={t} />
    </ellipse>
    <Chosen x={x} y={y} w={w} h={h} />
    <Walk k="ellipse" stops={[[x, y, 0.12], [x + w, y + h, DONE]]} />
  </>;
}

/** Polygon: drag and a six-sided shape grows from the press point. */
function PolygonDemo() {
  const [x, y, w, h] = [78, 14, 84, 84];
  const unit: Pt[] = Array.from({ length: 6 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    return [Math.cos(a), Math.sin(a)] as const;
  });
  const pts = fit(unit, x, y, w, h);
  return <>
    <path className="g-new" d={collapsed(pts, [x, y])}>
      <Anim attr="d" values={[collapsed(pts, [x, y]), collapsed(pts, [x, y]), outline(pts), outline(pts)]} times={[0, PRESS, DONE, 1]} />
    </path>
    <Chosen x={x} y={y} w={w} h={h} />
    <Walk k="polygon" stops={[[x, y, 0.12], [x + w, y + h, DONE]]} />
  </>;
}

/** Star: drag and a five-pointed star grows from the press point. */
function StarDemo() {
  const [x, y, w, h] = [76, 12, 88, 86];
  const unit: Pt[] = Array.from({ length: 10 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const r = i % 2 ? 0.42 : 1;
    return [Math.cos(a) * r, Math.sin(a) * r] as const;
  });
  const pts = fit(unit, x, y, w, h);
  return <>
    <path className="g-new" d={collapsed(pts, [x, y])}>
      <Anim attr="d" values={[collapsed(pts, [x, y]), collapsed(pts, [x, y]), outline(pts), outline(pts)]} times={[0, PRESS, DONE, 1]} />
    </path>
    <Chosen x={x} y={y} w={w} h={h} />
    <Walk k="star" stops={[[x, y, 0.12], [x + w, y + h, DONE]]} />
  </>;
}

/** Line: drag between two points. */
function LineDemo() {
  const a: Pt = [54, 88];
  const b: Pt = [186, 26];
  const t = [0, PRESS, DONE, 1];
  return <>
    <line className="g-path-new" x1={a[0]} y1={a[1]}>
      <Anim attr="x2" values={[a[0], a[0], b[0], b[0]]} times={t} />
      <Anim attr="y2" values={[a[1], a[1], b[1], b[1]]} times={t} />
      <Between from={PRESS} to={0.97} />
    </line>
    <g><Between from={DONE + 0.02} to={0.97} /><Handle x={a[0]} y={a[1]} /><Handle x={b[0]} y={b[1]} /></g>
    <Walk k="line" stops={[[a[0], a[1], 0.12], [b[0], b[1], DONE]]} />
  </>;
}

/** Arc: drag a box and a quarter curve bends across it. */
function ArcDemo() {
  const [x, y, w, h] = [58, 22, 124, 70];
  const k = 0.5522847498307936;
  const arc = (s: number) => `M${x} ${y}C${x + k * w * s} ${y} ${x + w * s} ${y + h * s - k * h * s} ${x + w * s} ${y + h * s}`;
  return <>
    <path className="g-path-new" d={arc(0)}>
      <Anim attr="d" values={[arc(0), arc(0), arc(1), arc(1)]} times={[0, PRESS, DONE, 1]} />
      <Between from={PRESS} to={0.97} />
    </path>
    <g><Between from={DONE + 0.02} to={0.97} /><Anchor x={x} y={y} /><Anchor x={x + w} y={y + h} /></g>
    <Walk k="arc" stops={[[x, y, 0.12], [x + w, y + h, DONE]]} />
  </>;
}

// ——— Type ———

/** Point type: click, a caret blinks, the word is typed letter by letter. */
function TextDemo() {
  const word = 'Hello';
  // Advance after each prefix at 22px in the UI face (measured in the lab).
  const widths = [0, 16.2, 28.4, 33.9, 39.3, 51.9];
  const x = 94;
  const base = 64;
  const letterAt = [0.32, 0.37, 0.42, 0.47, 0.52];
  const caretXs = widths.map((w) => x + w + 1.5);
  return <>
    {letterAt.map((at, i) => (
      <text key={i} className="g-text" x={x} y={base} style={{ fontSize: 22 }}>
        {word.slice(0, i + 1)}
        <Between from={at} to={i < word.length - 1 ? letterAt[i + 1]! : 0.97} />
      </text>
    ))}
    <line className="g-caret" x1={0} x2={0} y1={base - 18} y2={base + 4}>
      <StepMove at={caretXs.map((cx) => [cx, 0] as const)} times={[0, ...letterAt]} />
      <Steps attr="opacity" values={[0, 1, 0, 1, 0, 1, 0, 1, 0]} times={[0, 0.17, 0.23, 0.27, 0.62, 0.72, 0.8, 0.88, 0.97]} />
    </line>
    <Walk k="text" stops={[[172, 96, 0.02], [x, base - 7, 0.15], [x, base - 7, 0.2], [150, 96, 0.3]]} />
  </>;
}

/** Area type: drag a box, then the words fill it, wrapping onto a second line. */
function TextAreaDemo() {
  const [x, y, w, h] = [70, 22, 100, 66];
  const tx = x + 8;
  const lines: { y: number; parts: { text: string; w: number; at: number }[] }[] = [
    { y: y + 26, parts: [{ text: 'Type', w: 35.3, at: 0.5 }, { text: 'Type your', w: 71, at: 0.56 }] },
    { y: y + 46, parts: [{ text: 'words', w: 43.9, at: 0.64 }, { text: 'words here', w: 79.8, at: 0.7 }] },
  ];
  const all = lines.flatMap((line) => line.parts.map((part) => ({ ...part, y: line.y })));
  return <>
    <rect className="g-sel" x={x} y={y}>
      <Anim attr="width" values={[0, 0, w, w]} times={[0, PRESS, 0.44, 1]} />
      <Anim attr="height" values={[0, 0, h, h]} times={[0, PRESS, 0.44, 1]} />
      <Between from={PRESS} to={0.97} />
    </rect>
    {all.map((part, i) => (
      <text key={i} className="g-text" x={tx} y={part.y}>
        {part.text}
        <Between from={part.at} to={i + 1 < all.length && all[i + 1]!.y === part.y ? all[i + 1]!.at : 0.97} />
      </text>
    ))}
    <line className="g-caret" x1={0} x2={0} y1={-13} y2={3}>
      <StepMove at={[[tx, lines[0]!.y], ...all.map((p) => [tx + p.w + 1.5, p.y] as const)]} times={[0, ...all.map((p) => p.at)]} />
      <Steps attr="opacity" values={[0, 1, 0, 1, 0, 1, 0]} times={[0, 0.46, 0.76, 0.82, 0.88, 0.93, 0.97]} />
    </line>
    <Walk k="text:area" stops={[[x, y, 0.12], [x + w, y + h, 0.44]]} />
  </>;
}

/** A point `at` along a cubic, by arc length, with its heading in degrees. */
function alongCubic(p: readonly Pt[], at: number): { x: number; y: number; deg: number } {
  const b = (t: number): Pt => {
    const u = 1 - t;
    return [
      u * u * u * p[0]![0] + 3 * u * u * t * p[1]![0] + 3 * u * t * t * p[2]![0] + t * t * t * p[3]![0],
      u * u * u * p[0]![1] + 3 * u * u * t * p[1]![1] + 3 * u * t * t * p[2]![1] + t * t * t * p[3]![1],
    ];
  };
  let len = 0;
  let prev = b(0);
  for (let i = 1; i <= 400; i++) {
    const next = b(i / 400);
    len += Math.hypot(next[0] - prev[0], next[1] - prev[1]);
    if (len >= at) return { x: next[0], y: next[1], deg: (Math.atan2(next[1] - prev[1], next[0] - prev[0]) * 180) / Math.PI };
    prev = next;
  }
  return { x: prev[0], y: prev[1], deg: 0 };
}

/** Type on a path: press a curve and the words run along it. */
function TextPathDemo() {
  const id = `g-tp-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const ctrl: Pt[] = [[26, 94], [76, 16], [164, 16], [214, 94]];
  const d = `M${ctrl[0]![0]} ${ctrl[0]![1]}C${ctrl[1]![0]} ${ctrl[1]![1]} ${ctrl[2]![0]} ${ctrl[2]![1]} ${ctrl[3]![0]} ${ctrl[3]![1]}`;
  const words = 'Words ride the curve';
  const offset = 42;
  const start = alongCubic(ctrl, offset);
  const first = 0.3;
  const step = (0.7 - first) / words.length;
  return <>
    <path id={id} className="g-path" d={d} />
    {Array.from(words, (_, i) => (
      <text key={i} className="g-text" dy={-7}>
        <textPath href={`#${id}`} startOffset={offset}>{words.slice(0, i + 1)}</textPath>
        <Between from={first + i * step} to={i < words.length - 1 ? first + (i + 1) * step : 0.97} />
      </text>
    ))}
    <line className="g-caret" x1={0} x2={0} y1={-16} y2={2} transform={`translate(${start.x.toFixed(1)} ${start.y.toFixed(1)}) rotate(${start.deg.toFixed(1)})`}>
      <Steps attr="opacity" values={[0, 1, 0, 1, 0]} times={[0, 0.18, 0.23, 0.26, first]} />
    </line>
    <Walk k="text:path" stops={[[196, 104, 0.02], [start.x, start.y - 6, 0.15], [start.x, start.y - 6, 0.2], [120, 96, 0.3]]} />
  </>;
}

// ——— Artboard and slices ———

/** Artboard: drag out a page; its name tab appears over its corner. */
function ArtboardDemo() {
  const [x, y, w, h] = [58, 30, 124, 68];
  const t = [0, PRESS, DONE, 1];
  return <>
    <rect x={x} y={y} style={{ fill: '#fff', stroke: 'var(--accent)', strokeWidth: 1 }}>
      <Anim attr="width" values={[0, 0, w, w]} times={t} />
      <Anim attr="height" values={[0, 0, h, h]} times={t} />
    </rect>
    <g>
      <Between from={DONE + 0.03} to={0.97} />
      <rect className="g-muted" x={x} y={y - 11} width={34} height={6} rx={3} />
      <Handles x={x} y={y} w={w} h={h} />
    </g>
    <Walk k="artboard" stops={[[x, y, 0.12], [x + w, y + h, DONE]]} />
  </>;
}

/** The mark a slice carries: it comes out as its own file. */
function ExportBadge({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <rect x={x} y={y} width={13} height={13} rx={2.5} style={{ fill: 'var(--accent)' }} />
      <path d={`M${x + 6.5} ${y + 3}v5.5M${x + 4} ${y + 6}l2.5 2.5 2.5-2.5M${x + 3.5} ${y + 10.5}h6`} style={{ fill: 'none', stroke: '#fff', strokeWidth: 1.2, strokeLinecap: 'round', strokeLinejoin: 'round' }} />
    </g>
  );
}

/** The artwork both slice guides cut from. */
function SliceArt() {
  return <>
    <circle className="g-obj" cx={88} cy={56} r={24} />
    <rect className="g-obj" x={140} y={34} width={50} height={44} rx={4} />
  </>;
}

/** Slice: draw a dashed box over part of the artwork; it becomes a file of its own. */
function SliceDemo() {
  const [x, y, w, h] = [56, 22, 66, 70];
  const t = [0, PRESS, DONE, 1];
  return <>
    <SliceArt />
    <rect className="g-band" x={x} y={y}>
      <Anim attr="width" values={[0, 0, w, w]} times={t} />
      <Anim attr="height" values={[0, 0, h, h]} times={t} />
      <Between from={PRESS} to={0.97} />
    </rect>
    <g><Between from={DONE + 0.03} to={0.97} /><ExportBadge x={x} y={y} /></g>
    <Walk k="slice" stops={[[x, y, 0.12], [x + w, y + h, DONE]]} />
  </>;
}

/** Select slice: click a slice, it shows its corners; drag one and the slice grows. */
function SliceChooseDemo() {
  const [x, y, w, h] = [56, 22, 66, 70];
  const [w2, h2] = [144, 72];
  const pick = 0.26;
  const grab = 0.38;
  const let_go = 0.66;
  const t = [0, grab, let_go, 1];
  const corner = (cx: 'l' | 'r', cy: 't' | 'b') => (
    <rect className="g-handle" width={5} height={5} x={x - 2.5} y={y - 2.5}>
      {cx === 'r' ? <Anim attr="x" values={[x + w - 2.5, x + w - 2.5, x + w2 - 2.5, x + w2 - 2.5]} times={t} /> : null}
      {cy === 'b' ? <Anim attr="y" values={[y + h - 2.5, y + h - 2.5, y + h2 - 2.5, y + h2 - 2.5]} times={t} /> : null}
    </rect>
  );
  return <>
    <SliceArt />
    <rect className="g-band" x={x} y={y}>
      <Anim attr="width" values={[w, w, w2, w2]} times={t} />
      <Anim attr="height" values={[h, h, h2, h2]} times={t} />
      <Steps attr="stroke-width" values={[1, 2, 1]} times={[0, pick, 0.97]} />
    </rect>
    <ExportBadge x={x} y={y} />
    <g>
      <Between from={pick} to={0.97} />
      {corner('l', 't')}{corner('r', 't')}{corner('l', 'b')}{corner('r', 'b')}
    </g>
    <Walk k="slice:choose" stops={[[200, 104, 0.02], [x + w, y + 40, pick - 0.02], [x + w, y + 40, pick + 0.02], [x + w, y + h, grab], [x + w2, y + h2, let_go]]} />
  </>;
}

// ——— Measure ———

/** Measure: drag between two places; a dimension line and its length appear. */
function MeasureDemo() {
  const a: Pt = [60, 60];
  const b: Pt = [180, 60];
  const t = [0, PRESS, DONE, 1];
  return <>
    <rect className="g-obj" x={24} y={38} width={36} height={44} rx={3} />
    <rect className="g-obj" x={180} y={38} width={36} height={44} rx={3} />
    <g>
      <Between from={PRESS} to={0.97} />
      <line x1={a[0]} y1={a[1]} y2={b[1]} style={accentLine}>
        <Anim attr="x2" values={[a[0], a[0], b[0], b[0]]} times={t} />
      </line>
      <line x1={a[0]} x2={a[0]} y1={a[1] - 6} y2={a[1] + 6} style={accentLine} />
      <line y1={b[1] - 6} y2={b[1] + 6} style={accentLine}>
        <Anim attr="x1" values={[a[0], a[0], b[0], b[0]]} times={t} />
        <Anim attr="x2" values={[a[0], a[0], b[0], b[0]]} times={t} />
      </line>
    </g>
    <g><Between from={DONE + 0.02} to={0.97} /><Chip x={120} y={a[1] - 16} text="120" w={30} /></g>
    <Walk k="measure" stops={[[a[0], a[1], 0.12], [b[0], b[1], DONE]]} />
  </>;
}

/** Angular measure: drag one arm from a corner, click where the other goes; the angle shows. */
function MeasureAngularDemo() {
  const o: Pt = [58, 90];
  const arm1: Pt = [192, 90];
  const arm2: Pt = [130, 18];
  const armDone = 0.4;
  const aim = 0.6;
  const r = 30;
  const deg = Math.atan2(o[1] - arm2[1], arm2[0] - o[0]);
  const arcEnd: Pt = [o[0] + r * Math.cos(deg), o[1] - r * Math.sin(deg)];
  const mid = deg / 2;
  // A tick across the end of an arm, square to it.
  const tick = (end: Pt, dir: number) => {
    const nx = Math.sin(dir) * 6;
    const ny = Math.cos(dir) * 6;
    return `M${end[0] - nx} ${end[1] - ny}L${end[0] + nx} ${end[1] + ny}`;
  };
  return <>
    <path className="g-obj" d={`M${o[0]} ${o[1]}L${arm1[0] + 8} ${o[1]}L${o[0] + 80} ${o[1] - 80}Z`} />
    <g>
      <Between from={PRESS} to={0.97} />
      <line x1={o[0]} y1={o[1]} y2={o[1]} style={accentLine}>
        <Anim attr="x2" values={[o[0], o[0], arm1[0], arm1[0]]} times={[0, PRESS, armDone, 1]} />
      </line>
    </g>
    <g><Between from={armDone} to={0.97} /><path d={tick(arm1, 0)} style={accentLine} /></g>
    <g>
      <Between from={armDone + 0.04} to={0.97} />
      <line x1={o[0]} y1={o[1]} style={accentLine}>
        <Anim attr="x2" values={[arm1[0], arm1[0], arm2[0], arm2[0]]} times={[0, armDone + 0.04, aim, 1]} />
        <Anim attr="y2" values={[arm1[1], arm1[1], arm2[1], arm2[1]]} times={[0, armDone + 0.04, aim, 1]} />
      </line>
    </g>
    <g>
      <Between from={aim + 0.02} to={0.97} />
      <path d={tick(arm2, deg)} style={accentLine} />
      <path d={`M${o[0] + r} ${o[1]}A${r} ${r} 0 0 0 ${arcEnd[0].toFixed(1)} ${arcEnd[1].toFixed(1)}`} style={{ ...accentLine, strokeWidth: 1.2 }} />
      <Chip x={o[0] + 50 * Math.cos(mid)} y={o[1] - 50 * Math.sin(mid)} text="45°" w={30} />
    </g>
    <Walk k="measure:angular" stops={[[o[0], o[1], 0.12], [arm1[0], arm1[1], armDone], [arm1[0], arm1[1], armDone + 0.04], [arm2[0], arm2[1], aim]]} />
  </>;
}

/** Radial measure: click a circle; its radius is drawn and read out. */
function MeasureRadialDemo() {
  const [cx, cy, r] = [92, 56, 30];
  const click = 0.3;
  return <>
    <circle className="g-obj" cx={cx} cy={cy} r={r} />
    <g>
      <Between from={click} to={0.97} />
      <circle className="g-sel" cx={cx} cy={cy} r={r} />
      <circle className="g-accent" cx={cx} cy={cy} r={2.5} />
      <line x1={cx} y1={cy} y2={cy} style={accentLine}>
        <Anim attr="x2" values={[cx, cx, cx + r, cx + r]} times={[0, click + 0.02, 0.46, 1]} />
      </line>
    </g>
    <g><Between from={0.48} to={0.97} /><Chip x={cx + r + 26} y={cy} text="R 30" w={36} /></g>
    <Walk k="measure:radial" stops={[[196, 102, 0.04], [cx + 12, cy - 14, click - 0.02], [cx + 12, cy - 14, click + 0.06], [186, 98, 0.5]]} />
  </>;
}

/** Guides for the shapes group, by key (see ../index.ts). */
export const SHAPE_DEMOS: Record<string, ComponentType> = {
  'select:point': SelectPointDemo,
  'select:area': SelectAreaDemo,
  'select:lasso': SelectLassoDemo,
  ellipse: EllipseDemo,
  polygon: PolygonDemo,
  star: StarDemo,
  line: LineDemo,
  arc: ArcDemo,
  text: TextDemo,
  'text:area': TextAreaDemo,
  'text:path': TextPathDemo,
  artboard: ArtboardDemo,
  slice: SliceDemo,
  'slice:choose': SliceChooseDemo,
  measure: MeasureDemo,
  'measure:angular': MeasureAngularDemo,
  'measure:radial': MeasureRadialDemo,
};
